import { useEffect, useState } from 'react';
import * as session from 'modules/session';
import { analytics } from 'frontend/analytics/analytics';
import {
  getEcomDataToAnalytics,
  getFbPixelDataToCart,
} from 'frontend/analytics/analyticsHelper';
import { Breadcrumbs } from '@shared/ui/breadcrumbs';
import { PageContainer } from '@shared/ui/content-containers';
import { PageTitle } from '@shared/ui/titles';
import { Preloader } from '@shared/ui/preloader';
import { PageBanner, PageCustomText } from '@widgets/seoPage';
import { getCurrencySymbol } from '@entities/countries';
import { useLoaderData, useRouteLoaderData } from '@shared/lib/routing';
import { useParams } from 'react-router-dom';
import { prepareProducts } from 'src/server/helpers/products';
import Emitter from 'frontend/Components/NewCartOrder/Emitters';
import Card from 'frontend/Components/Products/Card/Card';
import ErrorPage from 'frontend/Pages/ErrorPage/ErrorPage';
import { ProductSinglePageLoader } from './api';
import { SingleProductHelmet } from './lib';

export function ProductSingle() {
  const { loading, pageData } =
    useLoaderData<Awaited<ReturnType<typeof ProductSinglePageLoader>>>();
  const { country, city, menu } = useRouteLoaderData('city-layout');
  const { menu: menuTextId } = useParams<{ menu: string }>();
  const { items = [] } = session.get('cart');
  const [cartItems, setCartItems] = useState(items);

  useEffect(() => {
    const cartChangeHandler = () => {
      setCartItems(session.get('cart').items || []);
    };
    Emitter.addListener('CART_CHANGE', cartChangeHandler);
    return () => {
      Emitter.removeListener('CART_CHANGE', cartChangeHandler);
    };
  }, []);

  useEffect(() => {
    if (!pageData?.productData) return;
    analytics.ecom(
      'detail',
      getEcomDataToAnalytics({ product: pageData.productData, qty: 1 }),
      country.currency,
    );
    analytics.fbPixel(
      'ViewContent',
      getFbPixelDataToCart(pageData.productData, country.currency),
    );
  }, []);

  const getCartItem = (productId: number) => {
    let cartItem;
    if (cartItems && Array.isArray(cartItems)) {
      cartItem = cartItems.find((item) => item.id === productId);
    }
    return cartItem;
  };

  if (loading) {
    return <Preloader />;
  }

  const selectedCategory = menu.find((c) => c.text_id === menuTextId);

  if (!selectedCategory || !pageData.productData) {
    return <ErrorPage />;
  }
  const { seoPageData, productData } = pageData;
  const currency = getCurrencySymbol(country.currency);
  const product: typeof productData = prepareProducts(productData);

  return (
    <>
      <PageBanner
        bannerData={seoPageData.banner}
        mainDomain={country.main_domain}
      />
      <SingleProductHelmet
        seo={seoPageData.seo}
        product={product}
        mainDomain={country.main_domain}
        currency={currency}
        cityName={city.city_name}
      />
      <PageContainer>
        <Breadcrumbs
          links={[
            {
              href: `/menu/${selectedCategory.text_id}`,
              text: selectedCategory.title,
            },
            {
              text: product.title,
            },
          ]}
        />
        <PageTitle isCenterOnMobile>{product.title}</PageTitle>
        <div
          itemScope
          itemType="http://schema.org/Product"
          className="mr-auto ml-auto max-w-[400px] lg:mr-0 lg:ml-0"
        >
          <Card
            product={product}
            view_type="single"
            position="productPage"
            cart_item={getCartItem(product.id)}
          />
        </div>
        <PageCustomText
          customText={seoPageData.seo.custom_text}
          mainDomain={country.currency}
        />
      </PageContainer>
    </>
  );
}
